import {
  Briefcase,
  Calendar,
  CheckCircle,
  Mail,
  MapPin,
  Phone,
  User,
  XCircle,
  Building2,
  Award,
  Crown,
  Shield,
} from "lucide-react";
import PageLoader from "../ui/PageLoader";
import { showToast } from "../ui/toast";
import { FaCheckCircle } from "react-icons/fa";
import { RxCrossCircled } from "react-icons/rx";
import moment from "moment-timezone";
import { FaFacebook, FaInstagram } from "react-icons/fa6";
import { useGetCompaniesQuery } from "apis/company";
import { useGetOfficesQuery } from "apis/office";
import { useAgencyPartyByIdQuery } from "apis/agencyParty";

export default function UserView({ user, isLoading, isError, error }) {
  // Fetch companies and offices data
  const {
    data: companies,
    isLoading: companiesLoading,
    error: companiesError,
  } = useGetCompaniesQuery();

  const {
    data: offices,
    isLoading: officesLoading,
    error: officesError,
  } = useGetOfficesQuery();

  // Conjunction agent (AgencyParty) — an id present means the user is a
  // conjunction agent. Fetched by id; the section below is gated on whether
  // it resolves.
  const { data: conjunctionDetails } = useAgencyPartyByIdQuery(
    user?.conjunctionAgentId,
    { enabled: !!user?.conjunctionAgentId },
  );

  if (isLoading || companiesLoading || officesLoading) {
    return <PageLoader />;
  }

  if (isError) {
    showToast(error.message, "error");
  }

  if (companiesError) {
    showToast(companiesError.message, "error");
  }

  if (officesError) {
    showToast(officesError.message, "error");
  }

  // Find user's company and office
  const userCompany = companies?.find(
    (company) => company._id === user.companyId,
  );
  const userOffice = offices?.find(
    (office) => office._id.toString() === user.officeId.toString(),
  );

  // Companies this user is the primary owner of, and whether they own any
  // company (primary or secondary owner) or only run one as an admin — all read
  // from the company's grants.
  const isUser = (person) => person?._id === user._id;
  const ownedCompanies =
    companies?.filter((company) => isUser(company.primaryOwner)) || [];
  const ownsCompany = (companies || []).some(
    (company) =>
      isUser(company.primaryOwner) || (company.coOwners || []).some(isUser),
  );
  const isCompanyAdminOnly =
    !ownsCompany &&
    (companies || []).some((company) =>
      (company.administrators || []).some(isUser),
    );

  // License now lives under user.license (nested object).
  const license = user.license || {};

  return (
    <div className="w-full max-w-5xl mx-auto bg-white">
      {/* Header Section */}
      <div className="border-b border-mediumgray pb-8 mb-8">
        <div className="flex flex-col md:flex-row items-start space-y-6 md:space-y-0 md:space-x-8">
          {/* Profile Image */}
          <div className="relative flex-shrink-0">
            <div className="h-32 w-32 rounded-full overflow-hidden bg-lightgray">
              <img
                src={user.picture}
                alt={user.name}
                className="h-full w-full object-cover"
              />
            </div>
            {ownsCompany && (
              <div className="absolute bottom-0 right-0 bg-blue-600 rounded-full p-1.5">
                <Crown className="w-4 h-4 text-white" />
              </div>
            )}
            {isCompanyAdminOnly && (
              <div className="absolute bottom-0 right-0 bg-blue-600 rounded-full p-1.5">
                <Shield className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between">
              <div>
                <h4 className="mb-1">{user.name}</h4>
                <p className="text-base text-darkgray mb-2">{user.title}</p>

                <div className="flex gap-2 items-start text-darkgray mb-3">
                  <Building2 className="w-4 h-4 flex-shrink-0" />
                  <span>{userCompany?.name}</span>
                </div>

                {/* Social Links */}
                {(user.facebookUrl || user.instagramUrl) && (
                  <div className="flex space-x-3">
                    {user.facebookUrl && (
                      <a
                        href={user.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-darkgray hover:text-blue-600"
                      >
                        <FaFacebook className="w-5 h-5" />
                      </a>
                    )}
                    {user.instagramUrl && (
                      <a
                        href={user.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-darkgray hover:text-blue-600"
                      >
                        <FaInstagram className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* Status Badges */}
              <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
                {ownsCompany && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <Crown className="w-3 h-3 mr-1" />
                    Company Owner
                  </span>
                )}
                {isCompanyAdminOnly && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <Shield className="w-3 h-3 mr-1" />
                    Company Admin
                  </span>
                )}
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    license.isValid
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {license.isValid ? (
                    <FaCheckCircle className="w-3 h-3 mr-1" />
                  ) : (
                    <RxCrossCircled className="w-3 h-3 mr-1" />
                  )}
                  {license.isValid ? "Licensed" : "Unlicensed"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="mb-8">
        <h4 className=" mb-4">Contact Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-3">
            <Mail className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Email Address
              </p>
              <p className="text-darkergray">{user.email}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Phone className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Mobile Number
              </p>
              <p className="text-darkergray">{user.mobile}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Calendar className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Joining Date
              </p>
              <p className="text-darkergray">
                {moment(user.createdAt)
                  .tz("Australia/Sydney")
                  .format("D MMM YYYY")}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Calendar className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Date of Birth
              </p>
              <p className="text-darkergray">
                {moment(user.birthDate)
                  .tz("Australia/Sydney")
                  .format("D MMM YYYY")}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <MapPin className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Company Address
              </p>
              <p className="text-darkergray">{userOffice?.address}</p>
            </div>
          </div>
        
        </div>
      </div>

      {/* License Information */}
      <div className="mb-8">
        <h4 className="mb-4">License Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-3">
            <Shield className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Type
              </p>
              <p className="text-darkergray capitalize">
                {license.type || "N/A"}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Shield className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                License Number
              </p>
              <p className="text-darkergray">{license.number || "N/A"}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <User className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Issued To
              </p>
              <p className="text-darkergray">{license.issuedTo || "N/A"}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Calendar className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Expiry Date
              </p>
              <p className="text-darkergray">
                {license.expiry
                  ? moment(license.expiry)
                      .tz("Australia/Sydney")
                      .format("D MMM YYYY")
                  : "N/A"}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            {license.isExpired ? (
              <RxCrossCircled className="w-4 h-4 text-red-600 mt-1" />
            ) : (
              <FaCheckCircle className="w-4 h-4 text-green-600 mt-1" />
            )}
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Expiry Status
              </p>
              <p className="text-darkergray">
                {license.isExpired ? "Expired" : "Active"}
              </p>
            </div>
          </div>
            <div className="flex items-start space-x-3">
            {license.isValid ? (
              <FaCheckCircle className="w-4 h-4 text-green-600 mt-1" />
            ) : (
              <RxCrossCircled className="w-4 h-4 text-red-600 mt-1" />
            )}
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                License Status
              </p>
              <p className="text-darkergray">
                {license.isValid ? "Valid" : "Invalid"}
              </p>
            </div>
          </div>
          {license.url && (
            <div className="flex items-start space-x-3">
              <Briefcase className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-darkgray uppercase mb-1">
                  Document
                </p>
                <a
                  href={license.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                >
                  View License
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company Information */}
      <div className="mb-8">
        <h4 className="mb-4">Company Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-3">
            <Building2 className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                Company Name
              </p>
              <p className="text-darkergray">{userCompany?.name || "N/A"}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Award className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                ABN
              </p>
              <p className="text-darkergray">{userCompany?.abn}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Shield className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                License Number
              </p>
              <p className="text-darkergray">{userCompany?.licenseNumber}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-4 h-4 text-darkgray mt-1 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-darkgray uppercase mb-1">
                GST Registration
              </p>
              <p className="text-darkergray">{userCompany?.gst || "N/A"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Owned Companies Section */}
      {ownedCompanies.length > 0 && (
        <div className="mb-8">
          <h4 className="mb-4">Owned Companies</h4>
          <div className="space-y-4">
            {ownedCompanies.map((company, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                      Company Name
                    </p>
                    <p className="text-darkergray font-medium">
                      {company.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                      ABN
                    </p>
                    <p className="text-darkergray">{company.abn}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                      License Number
                    </p>
                    <p className="text-darkergray">{company.licenseNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                      GST Registration
                    </p>
                    <p className="text-darkergray">{company.gst}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Digital Signature */}
      <div className="mb-8">
        <h4 className=" mb-4">Digital Signature</h4>
        <div className="border-2 border-dashed border-mediumgray rounded p-6 text-center">
          <img
            src={user.signature}
            alt="Digital Signature"
            className="w-full max-w-[200px] h-auto mx-auto"
          />
        </div>
      </div>

      {/* Conjunction Agent Details — sourced from the linked AgencyParty
          (user.conjunctionAgentId). */}
      {conjunctionDetails && (
        <div className="mb-8">
          <h4 className="mb-4">Conjunction Agent Details</h4>
          <div className="border-l-4 border-blue-500 pl-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                  Company
                </p>
                <p className="text-darkergray">{conjunctionDetails.company}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                  ABN
                </p>
                <p className="text-darkergray">{conjunctionDetails.abn}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                  GST Registered
                </p>
                <p className="text-darkergray">{conjunctionDetails.gst}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                  Address
                </p>
                <p className="text-darkergray">{conjunctionDetails.address}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                  Phone
                </p>
                <p className="text-darkergray">{conjunctionDetails.phone}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                  Email
                </p>
                <p className="text-darkergray">{conjunctionDetails.email}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase mb-1">
                  Licence Number
                </p>
                <p className="text-darkergray">
                  {conjunctionDetails.licenceNumber}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Allocated Suburbs */}
      <div>
        <h4 className="mb-4">Allocated Suburbs</h4>
        <div className="flex flex-wrap gap-2">
          {user.suburbs.map((suburb, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded text-sm bg-darkergray text-white"
            >
              <MapPin className="w-3 h-3 mr-1" />
              {suburb?.suburb} {suburb?.state} {suburb?.postcode}
            </span>
          ))}
        </div>
        {user.suburbs.length === 0 && (
          <p className="text-darkgray">No suburbs allocated</p>
        )}
      </div>
    </div>
  );
}
